import { Link, useLocation } from 'react-router-dom'
import { 
  Home, 
  Ship, 
  Globe, 
  FlaskConical, 
  Users, 
  Trophy,
  Shield,
  Crown,
  Zap,
  Settings,
  User,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  MessageSquare,
} from 'lucide-react'

interface User {
  id: string
  name: string
  email: string
  level: number
  faction?: string
}

interface SidebarProps {
  user: User | null
  collapsed: boolean
  onToggleCollapse: () => void
}

const Sidebar = ({ user, collapsed, onToggleCollapse }: SidebarProps) => {
  const location = useLocation()

  const navigationItems = [
    { path: '/dashboard', label: 'Command Center', icon: Home, category: 'main' },
    { path: '/fleet', label: 'Fleet Management', icon: Ship, category: 'main' },
    { path: '/galaxy', label: 'Galaxy Map', icon: Globe, category: 'main' },
    { path: '/research', label: 'Research', icon: FlaskConical, category: 'main' },
    { path: '/diplomacy', label: 'Diplomacy', icon: Users, category: 'main' },
    { path: '/combat', label: 'Combat', icon: Shield, category: 'main' },
    { path: '/market', label: 'Trade Market', icon: TrendingUp, category: 'economy' },
    { path: '/alliances', label: 'Alliances', icon: Users, category: 'economy' },
    { path: '/rankings', label: 'Rankings', icon: Trophy, category: 'social' },
    { path: '/intelligence', label: 'Intelligence', icon: Zap, category: 'social' },
    { path: '/community', label: 'Community', icon: MessageSquare, category: 'social' },
    { path: '/settings', label: 'Settings', icon: Settings, category: 'user' },
  ]

  const getCategoryTitle = (category: string) => {
    switch (category) {
      case 'main': return 'Command'
      case 'economy': return 'Economy'
      case 'social': return 'Social'
      case 'user': return 'Account'
      default: return ''
    }
  }

  const renderNavItem = (item: typeof navigationItems[0]) => {
    const Icon = item.icon
    const isActive = location.pathname === item.path
    
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`flex items-center ${collapsed ? 'justify-center px-2' : 'px-4'} py-3 mx-2 rounded-lg transition-all duration-200 group ${
          isActive
            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
            : 'text-gray-300 hover:text-white hover:bg-white/10'
        }`}
        title={collapsed ? item.label : ''}
      >
        <Icon className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} ${isActive ? 'text-white' : ''}`} />
        {!collapsed && (
          <span className="text-sm font-medium">{item.label}</span>
        )}
        {!collapsed && isActive && (
          <div className="w-2 h-2 bg-white rounded-full ml-auto"></div>
        )}
      </Link>
    )
  }

  const groupedItems = navigationItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = []
    }
    acc[item.category].push(item)
    return acc
  }, {} as Record<string, typeof navigationItems>)

  return (
    <div className={`fixed left-0 top-0 h-full bg-slate-900/95 backdrop-blur-md border-r border-white/10 transition-all duration-300 z-30 ${
      collapsed ? 'w-16' : 'w-64'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        {!collapsed && (
          <div className="flex items-center space-x-2">
            <Crown className="w-6 h-6 text-purple-400" />
            <span className="text-lg font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Crown of Suns
            </span>
          </div>
        )}
        <button
          onClick={onToggleCollapse}
          className="p-1 rounded-lg hover:bg-white/10 transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-gray-400" />
          )}
        </button>
      </div>

      {/* User Info */}
      {user && (
        <div className={`p-4 border-b border-white/10 ${collapsed ? 'text-center' : ''}`}>
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'}`}>
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">{user.name}</div>
                <div className="text-xs text-gray-400 truncate">Level {user.level} • {user.faction}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4">
        {Object.entries(groupedItems).map(([category, items]) => (
          <div key={category} className="mb-6">
            {!collapsed && (
              <div className="px-4 mb-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {getCategoryTitle(category)}
                </h3>
              </div>
            )}
            <nav className="space-y-1">
              {items.map(renderNavItem)}
            </nav>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-white/10">
        {!collapsed && (
          <div className="text-xs text-gray-500 text-center">
            Space Strategy Game
          </div>
        )}
      </div>
    </div>
  )
}

export default Sidebar